import { cookieStorage } from '../../utils/cookieStorage';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Menu,
    X,
    User as UserIcon,
    Monitor,
    LogOut,
    ChevronDown,
    MessageCircle,
    Sparkles,
    LogIn,
    Compass,
    Briefcase,
    Calendar,
    Users,
    Layers,
    BookOpen,
    ArrowRight,
    CheckCircle2,
} from 'lucide-react';
import logo from '../../assets/images/logo/logo.png';
import Loading from '../Loading';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { TierAvatar, getUserRingTier, TIER_CONFIGS, getProfessionColor } from '../../utils/userTier';
import { fetchRamasProfesionales } from '../../services/ramaProfesionalService';
import DarkModeToggle from '../common/DarkModeToggle';
import { getPlatformRoute, getProfileRoute } from '../../utils/profileRoute';

const logoDark = `${process.env.PUBLIC_URL}/logo-comarca-blanco.png?v=2`;

// ─── Paleta Oficial Co Marca Group ─────────────────────────────────
const BRAND = {
    purplePrimary: '#61257B',   // Púrpura principal
    magentaPrimary: '#C1348B',  // Magenta / Fucsia
    orangeAccent: '#EA8406',    // Naranja acento
    yellowAccent: '#FFD97C',    // Amarillo suave / Dorado
    pinkNeon: '#c1348b',        // Rosa Neón
    blueInst: '#61257b',        // Azul Institucional
    tealLight: '#8b3fa8',       // Turquesa
};

// ─── Estructura de Navegación ──────────────────────────────────────
interface SubItem {
    to: string;
    label: string;
    desc?: string;
    icon?: React.ReactNode;
}

interface NavLinkItem {
    to: string;
    label: string;
    submenu?: SubItem[];
}

const NAV_LINKS: NavLinkItem[] = [
    { to: '/', label: 'Inicio' },
    {
        to: '/nosotros',
        label: 'Nosotros',
        submenu: [
            { to: '/nosotros/historia', label: 'Historia', desc: 'Nuestra trayectoria, misión y valores', icon: <Compass size={16} /> },
            { to: '/nosotros/proyectos', label: 'Proyectos', desc: 'Casos de éxito y alianzas estratégicas', icon: <Briefcase size={16} /> },
        ],
    },
    {
        to: '/servicios',
        label: 'Servicios',
        submenu: [
            { to: '/servicios/cowork', label: 'Cowork', desc: 'Puestos flexibles y colaborativos con café ilimitado', icon: <Layers size={16} /> },
            { to: '/servicios/oficinas-privadas', label: 'Oficinas privadas', desc: 'Espacios corporativos con privacidad total', icon: <Briefcase size={16} /> },
            { to: '/servicios/salas-de-reunion', label: 'Salas de reunión', desc: 'Salas multimedia y pantallas para tus juntas', icon: <Calendar size={16} /> },
            { to: '/servicios/eventos', label: 'Eventos', desc: 'Auditorios y terrazas para conferencias', icon: <Sparkles size={16} /> },
        ],
    },
    {
        to: '/comunidad',
        label: 'Comunidad',
        submenu: [
            { to: '/comunidad/banco-profesionales', label: 'Banco de profesionales', desc: 'Catálogo de talento verificado y calificado', icon: <Users size={16} /> },
            { to: '/comunidad/miembros', label: 'Miembros', desc: 'Red activa de emprendedores y empresas', icon: <CheckCircle2 size={16} /> },
            { to: '/comunidad/networking', label: 'Networking', desc: 'Encuentros bimensuales y rondas de negocios', icon: <Sparkles size={16} /> },
        ],
    },
    { to: '/blog', label: 'Blog' },
    { to: '/contacto', label: 'Contáctanos' },
];

export const Header: React.FC = () => {
    const location = useLocation();
    const { user, isAuthenticated, logout } = useAuth();
    const { isDark } = useTheme();

    // Estados
    const [scrolled, setScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [hoveredNav, setHoveredNav] = useState<string | null>(null);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [storageTick, setStorageTick] = useState(0);

    // Referencias para cierre al click fuera
    const navRef = useRef<HTMLDivElement>(null);
    const profileRef = useRef<HTMLDivElement>(null);

    // Sincronización en tiempo real de profesión y categoría con cookieStorage
    useEffect(() => {
        const onStorage = () => setStorageTick(t => t + 1);
        window.addEventListener('cookie-preferences-changed', onStorage);
        return () => window.removeEventListener('cookie-preferences-changed', onStorage);
    }, []);

    const categoryCookie = cookieStorage.getItem(`user_categoria_${user?.id}`) || cookieStorage.getItem('user_categoria') || '';
    // Guardar únicamente el estilo de la categoría actual; el catálogo completo viene del servidor.
    useEffect(() => {
        fetchRamasProfesionales().then(res => {
            if (res && res.length > 0) {
                cookieStorage.setItem('comarca_ramas_cache', JSON.stringify(res.filter(item => String(item.id) === categoryCookie || item.codigo?.toLowerCase() === categoryCookie.toLowerCase() || item.nombre?.toLowerCase() === categoryCookie.toLowerCase()).slice(0, 1).map(({id, codigo, nombre, color, color_dark}) => ({id, codigo, nombre, color, color_dark}))));
                setStorageTick(t => t + 1);
            }
        }).catch(() => {});
    }, [categoryCookie]);

    // Scroll listener para elevar la sombra del glassmorphism
    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Cierre al hacer click fuera o presionar Esc
    useEffect(() => {
        const handleDown = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) {
                setOpenDropdown(null);
            }
            if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
                setIsProfileOpen(false);
            }
        };

        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpenDropdown(null);
                setIsProfileOpen(false);
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleDown);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleDown);
            document.removeEventListener('keydown', handleKey);
        };
    }, []);

    // Cerrar menú móvil al cambiar de ruta
    useEffect(() => {
        setIsMenuOpen(false);
        setOpenDropdown(null);
        setOpenMobileSubmenu(null);
    }, [location.pathname]);

    const [logoutError, setLogoutError] = useState('');
    const handleLogout = async () => {
        setLoading(true);
        try {
            setLogoutError('');
            await logout('/');
        } catch {
            setLogoutError('No se pudo cerrar la sesión. Intenta nuevamente.');
        } finally {
            setLoading(false);
            setIsProfileOpen(false);
        }
    };

    // Validación de ruta activa
    const isRouteActive = (path: string) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const profColor = getProfessionColor(user);
    const userInitial = user?.first_name ? user.first_name[0].toUpperCase() : (user?.email ? user.email[0].toUpperCase() : 'C');
    const userTier = getUserRingTier(user);
    const tierConfig = TIER_CONFIGS[userTier] || TIER_CONFIGS.comun;

    const isHeroTop = (location.pathname === '/') && !scrolled;

    if (isAuthenticated === null) return null;

    return (
        <>
            {/* ═══ BARRA ADAPTABLE: EFECTO ORUGA — se extiende y comprime ═══ */}
            <header
                ref={navRef}
                role="navigation"
                aria-label="Navegación principal"
                style={{
                    position: 'fixed',
                    left: '50%',
                    zIndex: isMenuOpen ? 999 : 50,
                    pointerEvents: 'none',
                    width: scrolled ? 'min(82%, 72rem)' : '100%',
                    top: scrolled ? '14px' : '0px',
                    transform: 'translateX(-50%)',
                    transition: 'width 850ms cubic-bezier(0.22, 1, 0.36, 1), top 850ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
            >
            {logoutError && <div role="alert" style={{background:'#fff1f2',color:'#991b1b',padding:12}}>{logoutError}</div>}
                <div
                    className={`pointer-events-auto w-full transition-all ${scrolled ? 'header-pill-scrolled' : 'header-bar-extended'}`}
                    style={{
                        borderRadius: scrolled ? '9999px' : '0px',
                        background: scrolled
                            ? (isDark ? 'rgba(17, 8, 32, 0.88)' : 'rgba(255, 255, 255, 0.82)')
                            : 'transparent',
                        backdropFilter: scrolled ? 'blur(20px)' : 'none',
                        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
                        borderBottom: scrolled
                            ? 'none'
                            : (isDark ? 'none' : '1px solid rgba(255, 255, 255, 0.15)'),
                        border: scrolled
                            ? (isDark ? '1px solid rgba(193, 52, 139, 0.38)' : '1px solid rgba(255, 255, 255, 0.6)')
                            : 'none',
                        boxShadow: scrolled
                            ? (isDark
                                ? '0 16px 40px rgba(0, 0, 0, 0.75), 0 0 25px rgba(193, 52, 139, 0.25)'
                                : '0 16px 36px -6px rgba(97, 37, 123, 0.14), 0 2px 10px rgba(0, 0, 0, 0.02)')
                            : 'none',
                        transition: 'border-radius 850ms cubic-bezier(0.22, 1, 0.36, 1), background 600ms ease, box-shadow 600ms ease, border 600ms ease',
                    }}
                >
                    <div
                        style={{
                            maxWidth: scrolled ? '100%' : 'none',
                            margin: '0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            padding: scrolled ? '6px 24px' : 'clamp(10px, 2vw, 14px) clamp(16px, 4vw, 64px)',
                            transition: 'padding 850ms cubic-bezier(0.22, 1, 0.36, 1)',
                        }}
                    >
                        
                        {/* ── 1. LADO IZQUIERDO: BRANDING & LOGOTIPO ── */}
                        <Link
                            to="/"
                            aria-label="Ir al inicio de Co Marca Group"
                            className="group flex items-center gap-2.5 flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c1348b] rounded-xl px-1 py-0.5 active:scale-95"
                            style={{ transition: 'transform 200ms ease' }}
                        >
                            <img
                                src={(isDark || isHeroTop) ? logoDark : logo}
                                alt="Co Marca Group"
                                style={{
                                    height: (isDark || isHeroTop) ? (scrolled ? '24px' : '30px') : (scrolled ? '26px' : '32px'),
                                    width: 'auto',
                                    objectFit: 'contain',
                                    opacity: 1,
                                    transition: 'height 850ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease',
                                }}
                                className="group-hover:scale-105"
                            />
                        </Link>

                        {/* ── 2. CENTRO: MENÚ DESKTOP (REACT BITS STYLE) ── */}
                        <nav
                            className="hidden lg:flex items-center gap-1 xl:gap-2 relative"
                            onMouseLeave={() => setHoveredNav(null)}
                        >
                            {NAV_LINKS.map((item) => {
                                const active = isRouteActive(item.to);
                                const isDropOpen = openDropdown === item.to;

                                return (
                                    <div
                                        key={item.to}
                                        className="relative"
                                        onMouseEnter={() => {
                                             setHoveredNav(item.to);
                                            if (item.submenu) setOpenDropdown(item.to);
                                        }}
                                        onMouseLeave={() => {
                                            if (item.submenu) setOpenDropdown(null);
                                        }}
                                    >
                                        {item.submenu ? (
                                            <button
                                                onClick={() => setOpenDropdown(prev => prev === item.to ? null : item.to)}
                                                aria-haspopup="true"
                                                aria-expanded={isDropOpen}
                                                className={`relative px-3.5 py-2 rounded-full text-[0.88rem] font-medium transition-colors duration-200 flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c1348b] select-none ${
                                                    active
                                                        ? (isDark || isHeroTop ? 'text-[#FFD97C] font-bold' : 'text-[#c1348b] font-semibold')
                                                        : (isDark || isHeroTop ? 'text-white/90 hover:text-[#FFD97C]' : 'text-slate-700 hover:text-[#c1348b]')
                                                }`}
                                            >
                                                {/* Indicador de pastilla con Framer Motion */}
                                                {hoveredNav === item.to && (
                                                    <motion.span
                                                        layoutId="nav-hover-pill"
                                                        className={`absolute inset-0 rounded-full ${
                                                            isDark
                                                                ? 'bg-gradient-to-r from-purple-500/25 via-pink-500/20 to-transparent'
                                                                : 'bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-transparent'
                                                        } -z-10`}
                                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.35 }}
                                                    />
                                                )}

                                                <span>{item.label}</span>
                                                <ChevronDown
                                                    size={14}
                                                    className={`transition-transform duration-250 ease-out ${
                                                        isDropOpen
                                                            ? (isDark ? 'rotate-180 text-[#FFD97C]' : 'rotate-180 text-[#c1348b]')
                                                            : (isDark ? 'text-slate-300' : 'text-slate-400')
                                                    }`}
                                                />

                                                {/* Punto indicador de activo */}
                                                {active && (
                                                    <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isDark ? 'bg-[#FFD97C]' : 'bg-[#c1348b]'}`} />
                                                )}
                                            </button>
                                        ) : (
                                            <Link
                                                to={item.to}
                                                className={`relative px-3.5 py-2 rounded-full text-[0.88rem] font-medium transition-colors duration-200 flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c1348b] select-none ${
                                                    active
                                                        ? (isDark || isHeroTop ? 'text-[#FFD97C] font-bold' : 'text-[#c1348b] font-semibold')
                                                        : (isDark || isHeroTop ? 'text-white/90 hover:text-[#FFD97C]' : 'text-slate-700 hover:text-[#c1348b]')
                                                }`}
                                            >
                                                {hoveredNav === item.to && (
                                                    <motion.span
                                                        layoutId="nav-hover-pill"
                                                        className={`absolute inset-0 rounded-full ${
                                                            isDark
                                                                ? 'bg-gradient-to-r from-purple-500/25 via-pink-500/20 to-transparent'
                                                                : 'bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-transparent'
                                                        } -z-10`}
                                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.35 }}
                                                    />
                                                )}

                                                <span>{item.label}</span>

                                                {active && (
                                                    <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isDark ? 'bg-[#FFD97C]' : 'bg-[#c1348b]'}`} />
                                                )}
                                            </Link>
                                        )}

                                        {/* ── Submenú Glassmorphism Desktop ── */}
                                        <AnimatePresence>
                                            {item.submenu && isDropOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                    exit={{ opacity: 0, y: 6, scale: 0.95 }}
                                                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                                                    className="absolute top-full left-1/2 -translate-x-1/2 pt-2.5 w-72 z-50"
                                                >
                                                    <div className={`rounded-2xl backdrop-blur-xl p-2 overflow-hidden shadow-2xl transition-colors ${
                                                        isDark
                                                            ? 'bg-[#150a24]/95 border border-purple-500/30 shadow-[0_24px_50px_rgba(0,0,0,0.85)]'
                                                            : 'bg-white/95 border border-purple-100/80 shadow-[0_20px_50px_rgba(97,37,123,0.15),0_4px_16px_rgba(0,0,0,0.03)]'
                                                    }`}>
                                                        <div className={`px-3 py-1.5 mb-1 border-b flex items-center justify-between ${
                                                            isDark ? 'border-purple-900/40' : 'border-purple-50'
                                                        }`}>
                                                            <span className={`text-[10px] font-extrabold tracking-wider uppercase ${
                                                                isDark ? 'text-[#FFD97C]' : 'text-[#c1348b]'
                                                            }`}>
                                                                {item.label}
                                                            </span>
                                                            <span className="text-[10px] text-slate-400 font-mono">
                                                                {item.submenu.length} opciones
                                                            </span>
                                                        </div>

                                                        <div className="flex flex-col gap-0.5">
                                                            {item.submenu.map((sub) => (
                                                                <Link
                                                                    key={sub.to}
                                                                    to={sub.to}
                                                                    onClick={() => setOpenDropdown(null)}
                                                                    className={`group flex items-start gap-3 p-2.5 rounded-xl transition-all duration-150 ${
                                                                        isDark
                                                                            ? 'hover:bg-purple-900/40 text-slate-200 hover:text-[#FFD97C]'
                                                                            : 'hover:bg-gradient-to-r hover:from-purple-50/80 hover:to-pink-50/60 text-slate-700 hover:text-[#c1348b]'
                                                                    }`}
                                                                >
                                                                    <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
                                                                        isDark
                                                                            ? 'bg-purple-950/70 text-[#FFD97C] group-hover:bg-[#c1348b] group-hover:text-white'
                                                                            : 'bg-purple-50 text-[#61257B] group-hover:bg-[#c1348b] group-hover:text-white'
                                                                    }`}>
                                                                        {sub.icon || <Compass size={15} />}
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className={`text-[0.84rem] font-semibold transition-colors leading-tight ${
                                                                            isDark
                                                                                ? 'text-slate-100 group-hover:text-[#FFD97C]'
                                                                                : 'text-slate-800 group-hover:text-[#c1348b]'
                                                                        }`}>
                                                                            {sub.label}
                                                                        </div>
                                                                        {sub.desc && (
                                                                            <p className={`text-[0.74rem] line-clamp-1 mt-0.5 font-normal ${
                                                                                isDark ? 'text-slate-400 group-hover:text-slate-300' : 'text-slate-500 group-hover:text-slate-600'
                                                                            }`}>
                                                                                {sub.desc}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </nav>

                        {/* ── 3. LADO DERECHO: ACCIONES, CTA Y USUARIO ── */}
                        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">

                            {/* Switch Modo Noche / Día */}
                            <div className="flex items-center" title="Alternar modo noche y día">
                                <DarkModeToggle fontSize="13px" />
                            </div>

                            {/* Sección Autenticado / Login */}
                            {isAuthenticated ? (
                                <div className="relative" ref={profileRef}>
                                    <button
                                        onClick={() => setIsProfileOpen(prev => !prev)}
                                        aria-haspopup="true"
                                        aria-expanded={isProfileOpen}
                                        title={`Mi cuenta — ${profColor.label}`}
                                        className="relative p-0.5 rounded-full hover:scale-105 active:scale-95 transition-transform duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c1348b]"
                                    >
                                        <TierAvatar
                                            size={38}
                                            initial={userInitial}
                                            bgColor={profColor.color}
                                            fotoUrl={user?.foto_url}
                                            isOpenOrHovered={isProfileOpen}
                                            user={user}
                                        />
                                    </button>

                                    {/* Dropdown de Perfil Glassmorphism */}
                                    <AnimatePresence>
                                        {isProfileOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 6, scale: 0.95 }}
                                                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                                                className={`absolute right-0 top-full mt-2.5 w-64 rounded-2xl backdrop-blur-xl border overflow-hidden z-50 transition-colors ${
                                                    isDark
                                                        ? 'bg-[#150a24]/95 border-purple-500/30 shadow-[0_24px_50px_rgba(0,0,0,0.85)]'
                                                        : 'bg-white/95 border-purple-100 shadow-[0_20px_50px_rgba(97,37,123,0.18),0_4px_16px_rgba(0,0,0,0.04)]'
                                                }`}
                                            >
                                                {/* Header del perfil con gradiente personalizado de rol */}
                                                <div
                                                    className="p-4 flex items-center gap-3 text-white"
                                                    style={{
                                                        background: `linear-gradient(135deg, ${profColor.color}, ${profColor.colorDark})`,
                                                    }}
                                                >
                                                    <TierAvatar
                                                        size={42}
                                                        initial={userInitial}
                                                        bgColor={profColor.color}
                                                        fotoUrl={user?.foto_url}
                                                        isOpenOrHovered={false}
                                                        user={user}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-bold truncate leading-tight">
                                                            {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email : 'Usuario'}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                            <span className="text-[10px] bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full font-extrabold uppercase tracking-wide">
                                                                {tierConfig.iconText} {tierConfig.label}
                                                            </span>
                                                            <span className="text-[10px] bg-black/25 backdrop-blur-sm px-2 py-0.5 rounded-full font-bold tracking-wide">
                                                                {profColor.label}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Links de usuario */}
                                                <div className={`p-2 flex flex-col gap-1 transition-colors ${isDark ? 'bg-[#150a24]' : 'bg-white'}`}>
                                                    <Link
                                                        to={getProfileRoute(user)}
                                                        onClick={() => setIsProfileOpen(false)}
                                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                                                            isDark
                                                                ? 'text-slate-200 hover:bg-purple-900/40 hover:text-[#FFD97C]'
                                                                : 'text-slate-700 hover:bg-purple-50 hover:text-[#61257B]'
                                                        }`}
                                                    >
                                                        <UserIcon size={15} className="text-slate-400" />
                                                        <span>Mi Perfil</span>
                                                    </Link>

                                                    <Link
                                                        to={getPlatformRoute(user)}
                                                        onClick={() => setIsProfileOpen(false)}
                                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                                                            isDark
                                                                ? 'text-slate-200 hover:bg-purple-900/40 hover:text-[#FFD97C]'
                                                                : 'text-slate-700 hover:bg-purple-50 hover:text-[#61257B]'
                                                        }`}
                                                    >
                                                        <Monitor size={15} className="text-slate-400" />
                                                        <span>Plataforma</span>
                                                    </Link>

                                                    <div className={`h-px my-1 ${isDark ? 'bg-purple-900/40' : 'bg-slate-100'}`} />

                                                    <button
                                                        onClick={handleLogout}
                                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors w-full text-left ${
                                                            isDark ? 'text-rose-400 hover:bg-rose-950/40' : 'text-rose-600 hover:bg-rose-50'
                                                        }`}
                                                    >
                                                        <LogOut size={15} className="text-rose-500" />
                                                        <span>Cerrar sesión</span>
                                                    </button>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <div className="hidden sm:flex items-center gap-1.5">
                                    <Link
                                        to="/login"
                                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all duration-150 ${
                                            isDark || isHeroTop
                                                ? 'text-white/90 hover:text-[#FFD97C] hover:bg-white/10'
                                                : 'text-slate-700 hover:text-[#61257B] hover:bg-purple-50/80'
                                        }`}
                                    >
                                        <LogIn size={14} className={isDark || isHeroTop ? "text-[#FFD97C]" : "text-[#61257B]"} />
                                        <span>Ingresar</span>
                                    </Link>
                                    <Link
                                        to="/register"
                                        className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-xs font-bold transition-all duration-200 shadow-sm ${
                                            isDark
                                                ? 'text-white bg-gradient-to-r from-[#61257B] to-[#C1348B] hover:brightness-110'
                                                : 'text-white bg-slate-900 hover:bg-[#61257B]'
                                        }`}
                                    >
                                        <span>Registro</span>
                                    </Link>
                                </div>
                            )}

                            {/* ── BOTÓN HAMBURGUESA MÓVIL ── */}
                            <button
                                onClick={() => setIsMenuOpen(prev => !prev)}
                                aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                                aria-expanded={isMenuOpen}
                                className={`lg:hidden p-2 rounded-xl border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c1348b] active:scale-95 ${
                                    isDark
                                        ? 'text-slate-200 bg-purple-950/70 border-purple-500/30 hover:bg-purple-900/50 hover:text-[#FFD97C]'
                                        : 'text-slate-700 hover:text-[#c1348b] bg-white/80 border-slate-200/80 hover:bg-purple-50/70'
                                }`}
                            >
                                {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
                            </button>
                        </div>
                    </div>
                </div>

            </header>

            {/* ═══ MENÚ MÓVIL EN PORTAL (100% SÓLIDO EN MODO OSCURO, ULTRA-ESTÉTICO EN MODO CLARO) ═══ */}
            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {isMenuOpen && (
                        <div className="fixed inset-0 z-[99999] lg:hidden flex flex-col justify-start">
                            {/* 1. Backdrop Overlay que cubre el 100% de la pantalla sin ser atrapado por transform */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.22 }}
                                onClick={() => setIsMenuOpen(false)}
                                className="fixed inset-0 pointer-events-auto"
                                style={{
                                    background: isDark ? 'rgba(3, 1, 8, 0.92)' : 'rgba(15, 23, 42, 0.55)',
                                    backdropFilter: 'blur(16px)',
                                    WebkitBackdropFilter: 'blur(16px)',
                                }}
                            />

                            {/* 2. Tarjeta del Menú Móvil */}
                            <motion.div
                                initial={{ opacity: 0, y: -20, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -16, scale: 0.98 }}
                                transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                                className="mobile-nav-drawer pointer-events-auto relative z-10 w-[94%] max-w-lg mx-auto mt-3 sm:mt-5 rounded-[28px] overflow-hidden flex flex-col max-h-[88vh]"
                                style={{
                                    background: isDark ? '#110820' : '#ffffff',
                                    border: isDark
                                        ? '1.5px solid rgba(193, 52, 139, 0.45)'
                                        : '1.5px solid rgba(97, 37, 123, 0.12)',
                                    boxShadow: isDark
                                        ? '0 30px 80px -10px rgba(0, 0, 0, 0.98), 0 0 45px rgba(193, 52, 139, 0.3)'
                                        : '0 25px 70px -15px rgba(97, 37, 123, 0.2), 0 10px 30px rgba(0, 0, 0, 0.06)',
                                }}
                            >
                                {/* Header del Drawer */}
                                <div className={`px-5 py-4 flex items-center justify-between border-b ${
                                    isDark ? 'border-purple-900/30 bg-[#150a28]' : 'border-purple-50 bg-gradient-to-r from-purple-50/70 via-white to-pink-50/50'
                                }`}>
                                    <div className="flex items-center gap-2.5">
                                        <img
                                            src={isDark ? logoDark : logo}
                                            alt="Co Marca Group"
                                            className={`${isDark ? 'h-7' : 'h-10'} w-auto object-contain`}
                                            style={{
                                                opacity: 1,
                                                transition: 'opacity 300ms ease',
                                            }}
                                        />
                                        <span style={{
                                            fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em',
                                            color: isDark ? '#FFD97C' : '#61257B', textTransform: 'uppercase'
                                        }}>
                                            Navegación
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <DarkModeToggle fontSize="12px" />
                                        <button
                                            onClick={() => setIsMenuOpen(false)}
                                            aria-label="Cerrar menú"
                                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                                                isDark
                                                    ? 'bg-purple-950/80 text-slate-300 hover:bg-purple-900 hover:text-white border border-purple-800/40'
                                                    : 'bg-white text-slate-600 hover:bg-purple-50 hover:text-[#c1348b] border border-slate-200/80 shadow-sm'
                                            }`}
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>

                                {/* Links de navegación */}
                                <div className="overflow-y-auto px-4 py-3.5 flex flex-col gap-1.5 flex-1">
                                    {NAV_LINKS.map((item) => {
                                        const active = isRouteActive(item.to);
                                        const isSubExpanded = openMobileSubmenu === item.to;

                                        if (item.submenu) {
                                            return (
                                                <div
                                                    key={item.to}
                                                    className={`rounded-2xl transition-all overflow-hidden ${
                                                        isDark
                                                            ? (isSubExpanded ? 'bg-purple-950/40 border border-purple-800/30' : 'hover:bg-purple-950/25')
                                                            : (isSubExpanded ? 'bg-purple-50/60 border border-purple-100/80' : 'hover:bg-purple-50/40')
                                                    }`}
                                                >
                                                    <button
                                                        onClick={() => setOpenMobileSubmenu(prev => prev === item.to ? null : item.to)}
                                                        className={`w-full flex items-center justify-between px-3.5 py-3 rounded-2xl text-sm font-semibold transition-colors ${
                                                            active
                                                                ? (isDark ? 'text-[#FFD97C]' : 'text-[#61257B]')
                                                                : (isDark ? 'text-slate-200' : 'text-slate-800')
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2.5">
                                                            {active && (
                                                                <span className={`w-1.5 h-4 rounded-full ${isDark ? 'bg-[#FFD97C]' : 'bg-[#c1348b]'}`} />
                                                            )}
                                                            <span className="tracking-tight">{item.label}</span>
                                                        </div>
                                                        <div className={`w-7 h-7 rounded-xl flex items-center justify-center transition-transform duration-250 ${
                                                            isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100/70 text-[#61257B]'
                                                        } ${isSubExpanded ? 'rotate-180' : ''}`}>
                                                            <ChevronDown size={15} />
                                                        </div>
                                                    </button>

                                                    <AnimatePresence>
                                                        {isSubExpanded && (
                                                            <motion.div
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{ duration: 0.22, ease: 'easeInOut' }}
                                                                className="overflow-hidden px-2.5 pb-2.5 flex flex-col gap-1"
                                                            >
                                                                {item.submenu.map((sub) => (
                                                                    <Link
                                                                        key={sub.to}
                                                                        to={sub.to}
                                                                        onClick={() => setIsMenuOpen(false)}
                                                                        className={`flex items-start gap-3 p-2.5 rounded-xl text-xs transition-all ${
                                                                            isDark
                                                                                ? 'hover:bg-purple-900/50 text-slate-300 hover:text-white'
                                                                                : 'hover:bg-white text-slate-600 hover:text-[#61257B] hover:shadow-sm'
                                                                        }`}
                                                                    >
                                                                        <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                                            isDark ? 'bg-purple-900/60 text-[#FFD97C]' : 'bg-purple-100/70 text-[#61257B]'
                                                                        }`}>
                                                                            {sub.icon || <Compass size={13} />}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="font-bold">{sub.label}</div>
                                                                            {sub.desc && (
                                                                                <div className={`text-[11px] line-clamp-1 mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                                    {sub.desc}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </Link>
                                                                ))}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            );
                                        }

                                        return (
                                            <Link
                                                key={item.to}
                                                to={item.to}
                                                onClick={() => setIsMenuOpen(false)}
                                                className={`flex items-center justify-between px-3.5 py-3 rounded-2xl text-sm font-semibold transition-all ${
                                                    active
                                                        ? (isDark
                                                            ? 'text-[#FFD97C] bg-gradient-to-r from-purple-950/80 to-purple-900/40 border border-purple-800/40 shadow-sm'
                                                            : 'text-[#61257B] bg-gradient-to-r from-purple-50 to-pink-50/70 border border-purple-100/80 shadow-sm')
                                                        : (isDark
                                                            ? 'text-slate-200 hover:bg-purple-950/30'
                                                            : 'text-slate-800 hover:bg-purple-50/50')
                                                }`}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {active && (
                                                        <span className={`w-1.5 h-4 rounded-full ${isDark ? 'bg-[#FFD97C]' : 'bg-[#c1348b]'}`} />
                                                    )}
                                                    <span className="tracking-tight">{item.label}</span>
                                                </div>
                                                {active && (
                                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                                        isDark ? 'bg-[#FFD97C]/15 text-[#FFD97C]' : 'bg-[#c1348b]/10 text-[#c1348b]'
                                                    }`}>
                                                        Activo
                                                    </span>
                                                )}
                                            </Link>
                                        );
                                    })}
                                </div>

                                {/* Footer del drawer */}
                                <div className={`p-4 border-t flex flex-col gap-3 ${
                                    isDark ? 'border-purple-900/30 bg-[#0e061b]' : 'border-purple-50 bg-slate-50/50'
                                }`}>
                                    <div className={`flex items-center justify-between px-3.5 py-2.5 rounded-2xl ${
                                        isDark ? 'bg-purple-950/40 border border-purple-800/30' : 'bg-white border border-slate-200/70 shadow-sm'
                                    }`}>
                                        <div className="flex items-center gap-2.5">
                                            <span className="text-base">{isDark ? '🌙' : '☀️'}</span>
                                            <div>
                                                <div className={`text-xs font-bold leading-tight ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                                                    {isDark ? 'Modo Oscuro' : 'Modo Claro'}
                                                </div>
                                                <div className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                    Alternar apariencia
                                                </div>
                                            </div>
                                        </div>
                                        <DarkModeToggle fontSize="13px" />
                                    </div>

                                    {!isAuthenticated ? (
                                        <div className="grid grid-cols-2 gap-2.5">
                                            <Link
                                                to="/login"
                                                onClick={() => setIsMenuOpen(false)}
                                                className={`flex items-center justify-center gap-2 py-3 px-3 rounded-2xl text-xs font-bold transition-all border ${
                                                    isDark
                                                        ? 'text-[#FFD97C] border-purple-700/50 bg-purple-950/50 hover:bg-purple-900/40'
                                                        : 'text-[#61257B] border-[#61257B]/30 bg-white hover:bg-purple-50 shadow-sm'
                                                }`}
                                            >
                                                <LogIn size={14} />
                                                <span>Ingresar</span>
                                            </Link>
                                            <Link
                                                to="/register"
                                                onClick={() => setIsMenuOpen(false)}
                                                className="flex items-center justify-center gap-2 py-3 px-3 rounded-2xl text-xs font-bold text-white transition-all shadow-md active:scale-95"
                                                style={{
                                                    background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                                                    boxShadow: '0 4px 16px rgba(193, 52, 139, 0.35)',
                                                }}
                                            >
                                                <span>Registrarse</span>
                                                <ArrowRight size={14} />
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-2.5">
                                            {/* Tarjeta de usuario móvil con aro y color de profesión */}
                                            <div className={`p-3 rounded-2xl flex items-center gap-3 border ${
                                                isDark ? 'bg-purple-950/40 border-purple-800/40' : 'bg-white border-slate-200/80 shadow-sm'
                                            }`}>
                                                <TierAvatar
                                                    size={40}
                                                    initial={userInitial}
                                                    bgColor={profColor.color}
                                                    fotoUrl={user?.foto_url}
                                                    user={user}
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className={`text-xs font-bold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                                        {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email : 'Usuario'}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        <span
                                                            className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded-md"
                                                            style={{
                                                                background: `${profColor.color}25`,
                                                                color: profColor.color,
                                                                border: `1px solid ${profColor.color}45`,
                                                            }}
                                                        >
                                                            {profColor.label}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <Link
                                                to={getPlatformRoute(user)}
                                                onClick={() => setIsMenuOpen(false)}
                                                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl text-xs font-bold text-white bg-gradient-to-r from-[#61257B] to-[#C1348B] shadow-md"
                                            >
                                                <Monitor size={14} />
                                                <span>Ir a mi Plataforma</span>
                                            </Link>
                                            <button
                                                onClick={() => { setIsMenuOpen(false); handleLogout(); }}
                                                className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold text-rose-500 hover:bg-rose-500/10"
                                            >
                                                <LogOut size={14} />
                                                <span>Cerrar sesión</span>
                                            </button>
                                        </div>
                                    )}

                                    <div className="text-center text-[10px] text-slate-400 font-medium">
                                        Co Marca Group · Calacoto, La Paz
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>,
                document.body
            )}

            {loading && <Loading text="Cerrando sesión..." fullScreen={true} />}
        </>
    );
};

export default Header;

